import React from 'react';
import {
  findAllFormErrors,
  formExitDialog,
  findElementsWithoutValue,
  isDocumentFormValid,
  isFormReadyToAccept,
  isFormReadyToSignature,
  recalculateFormWidgetsIndexes,
  resetFormDialog,
  checkIfFormDoesNotNeedConfirm,
  getConfirmRestSignaturesMessage,
  setFormReadonlyMode,
  prepareDocumentsForFormValidation
} from '../helpers/formUtils';
import {
  clearExternalApiValidationStamp,
  getExternalApiFormActionTitleKey,
  getExternalApiValidationConfig,
  isExternalApiValidationStampValid,
  isFormItemInExternalApiValidation,
  runExternalApiValidation,
  withExternalApiValidationStamp
} from '../helpers/externalApiValidation';
import PropTypes from 'prop-types';
import {
  setDocumentCollectBioDataConfirmed,
  setSignatureCache,
  setSignatureTouchData
} from '../../redux/actions/rootAction';
import { FORMELEMENTS_SIGNATURE } from '../FormBuilder/FormElements';
import { xconnect } from '../../redux/xconnect';
import { DOC_TYPE_FORM, shallowEqual } from '../helpers/utils';
import { isElementAdvancedSignature } from '../../redux/utils';
import { gae, gaeApp } from '../helpers/Analytics';
import II18 from '../translate/II18';
import { alertDialog } from '../helpers/dialogUtils';
import { f7 } from 'framework7-react';

export default function withForms(WrappedComponent) {
  // noinspection JSPotentiallyInvalidUsageOfThis
  class WithFormsWrapper extends React.Component {
    constructor(props) {
      super(props);

      // State first: prepareLocalData merges externalApiFormErrors from
      // this.state. After merge with dev, prepareLocalData ran before state
      // and threw on mount — form (and Done) never rendered.
      this.state = {
        formWasChanged: false,
        currentPage: 0,
        showActiveAnimation: false,
        showRequiredErrors: false,
        moveToFirstError: false,
        externalApiFormErrors: new Map(),
        showStartButton: true,
        // Bumped when Zwischenstand / standalone draft is applied so PDF/QF
        // remounts: active field was uncontrolled (activeMode) and kept the
        // empty peach style until click.
        formRenderKey: 0
      };

      this.prepareLocalData();

      this.state = {
        ...this.state,
        showStartButton:
          this.getFormErrors().size > 0 && !props.documentWasVisited
      };
    }

    prepareLocalData = () => {
      const preparedFormStructure = this.props.preparedFormsStructures.get(
        this.props.document.id
      ) || [[]];

      const formWidgetsIds = new Set();
      preparedFormStructure.forEach(page =>
        page.forEach(widget => formWidgetsIds.add(widget.id))
      );

      //fix broken smartFill structure
      this.formStructure = this.props.smartFillFormMode
        ? this.props.document.smartFill?.map(smartPage =>
            smartPage.filter(
              smartWidget =>
                smartWidget.smartFillParagraph ||
                formWidgetsIds.has(smartWidget.id)
            )
          ) || [[]]
        : preparedFormStructure;

      //skip pages without widgets for smartFill
      if (
        this.props.smartFillFormMode ||
        this.props.document.type === DOC_TYPE_FORM
      ) {
        this.formStructure = this.formStructure.filter(p => p.length > 0);
        if (this.formStructure.length === 0) {
          this.formStructure = [[]];
        }
      }
      this.formStructure = recalculateFormWidgetsIndexes(this.formStructure);

      if (this.props.document.submitted || this.props.document.finalised) {
        this.formStructure = setFormReadonlyMode(
          this.formStructure,
          !this.props.document.submitted
        );
      }

      this.formResult =
        this.props.preparedFormsData.get(this.props.document.id) || new Map();

      this.formErrors = this.props.submitted
        ? new Map()
        : this.findAllFormErrors();

      if (this.state?.externalApiFormErrors?.size) {
        this.state.externalApiFormErrors.forEach((error, itemId) =>
          this.formErrors.set(itemId, { ...error, apiError: true })
        );
      }
    };

    findAllFormErrors = () => {
      return findAllFormErrors(
        this.formStructure,
        this.formResult,
        this.props.requestedDocuments,
        this.props.document?.id,
        this.props.preparedFormsData
      );
    };

    /**
     * docsForValidation + live overlay: all forms' values on
     * `attributes.formData` so cross-doc CONDITION placeholders
     * (e.g. PDF EQ vs QF.Text REF) resolve while editing.
     */
    getRequestedDocumentsWithLiveFormData = () => {
      return prepareDocumentsForFormValidation(
        this.props.requestedDocuments,
        this.props.preparedFormsData,
        this.formResult,
        this.props.document?.id
      );
    };

    /**
     * @returns Map
     */
    getFormErrors = () => this.formErrors || new Map();

    componentDidMount() {
      this.setState({
        showStartButton:
          this.getFormErrors().size > 0 && !this.props.documentWasVisited
      });
    }

    closeExternalApiValidationDialog = () => {
      f7.dialog.close();
    };

    componentDidUpdate(prevProps) {
      this.prepareLocalData();

      if (!prevProps.requestIsRestored && this.props.requestIsRestored) {
        this.setState(state => ({
          formRenderKey: state.formRenderKey + 1,
          showStartButton: false,
          showRequiredErrors: false,
          moveToFirstError: false
        }));
      }

      if (
        !shallowEqual(prevProps.preparedFormsData, this.props.preparedFormsData)
      ) {
        this.forceUpdate();
      }
      if (
        this.props.documentIsQualifiedSigned &&
        !prevProps.documentIsQualifiedSigned
      ) {
        this.onDocumentESignatureSuccess();
      }
    }

    onDocumentESignatureSuccess = () => {
      //Qualified signature has no data, we need populate formData with some value
      //document is finalised, and has only one required element
      const signatureElement = this.getFirstErrorElement();
      signatureElement &&
        this.updateFormResult(signatureElement.element, 'qes_signed');
    };

    cancelMoveToFirstError = () => {
      this.setState({
        moveToFirstError: false
      });
    };

    scrollToSignatureElement = signatureId => {
      setTimeout(function() {
        const signatureItem = document.getElementById(
          'signatureEnd_' + signatureId
        );
        if (signatureItem) {
          signatureItem.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'start'
          });
        }
      }, 100);
    };

    onReceiveSignature = (item, signature, data) => {
      this.updateFormResult(item, signature, data);
      this.onStopSignatureProcess();
      this.scrollToSignatureElement(item.id);
    };

    updateFormResult = (item, value, data) => {
      let newData = new Map(this.props.formData);
      newData.set(item.id, value);

      if (isFormItemInExternalApiValidation(this.props.document, item)) {
        newData = clearExternalApiValidationStamp(newData);
      }

      if (item.element === FORMELEMENTS_SIGNATURE && value) {
        this.props.setSignatureCache(value);
        if (
          data &&
          isElementAdvancedSignature(item) &&
          item.collectBiometricData
        ) {
          this.props.setSignatureTouchData(
            this.props.document.id,
            item.id,
            data
          );
        }
      }

      const extApiValidateError = this.state.externalApiFormErrors?.get(
        item.id
      );
      if (extApiValidateError) {
        this.setState({
          formWasChanged: true,
          externalApiFormErrors: new Map(
            this.state.externalApiFormErrors
          ).set(item.id, { ...extApiValidateError, updated: true })
        });
      }
      this.setState({
        formWasChanged: true
      });

      this.props.setDocumentForm(this.props.document.id, newData);

      return this.prepareFormItem(item, value);
    };

    hideStartButton = () => {
      this.setState({
        showStartButton: false
      });
    };

    onStopSignatureProcess = () => {
      this.setState({
        signatureProcessItem: null
      });
    };

    getFirstErrorElement = () => {
      if (!this.getFormErrors().size) return false;

      let firstErr = null;
      Array.from(this.getFormErrors()).forEach(entry => {
        if (!firstErr) {
          firstErr = entry[1];
        } else {
          if (
            entry[1].element.pageIndex < firstErr.element.pageIndex ||
            (entry[1].element.pageIndex === firstErr.element.pageIndex &&
              entry[1].element.elementIndex < firstErr.element.elementIndex)
          ) {
            firstErr = entry[1];
            firstErr.element = this.prepareFormItem(firstErr.element);
          }
        }
      });
      return firstErr;
    };

    isFormValid = () => {
      if (this.props.submitted) return true;
      return !Array.from(this.getFormErrors().values()).some(
        error => !error.apiError
      );
    };

    arePagesIsValid = () => {
      if (this.formStructure.length === 1) return [this.isFormValid()];

      const pages = this.formStructure.map(() => true);

      Array.from(this.getFormErrors()).forEach(entry => {
        pages[entry[1].element.pageIndex] = false;
      });
      return pages;
    };

    checkDataAndTryBack = showReset => {
      if (this.isFormValid()) {
        this.goBack();
      } else {
        formExitDialog(
          this.goBack,
          this.continueEditForm,
          showReset === true ? this.resetForm : null
        );
      }
    };

    continueEditForm = () => {
      this.setState({
        showRequiredErrors: true,
        moveToFirstError: true
      });
    };

    tryBack = () => {
      if (this.state.formWasChanged) {
        this.checkDataAndTryBack(true);
      } else this.goBack();
    };

    resetForm = () => {
      resetFormDialog(() => {
        this.formResult = new Map();
        this.goBack();
      });
    };

    resetFormNow = () => {
      resetFormDialog(() => {
        this.formResult = new Map();
      });
    };

    goBack = () => {
      this.props.navigateBack();
    };

    prepareFormItem = (item, newValue) => {
      if (item) {
        item.value =
          newValue !== undefined ? newValue : this.formResult.get(item.id);
      }

      return item;
    };

    /**
     * Find next input element on page
     * @param currentPage
     * @param activeInputIdx
     * @param onDone - back function:
     *          item or false
     */
    goNextElement = (currentPage, activeInputIdx, onDone) => {
      const pageElements = this.formStructure[currentPage];
      const idx = activeInputIdx + 1;

      if (idx < pageElements.length) {
        const item = pageElements[idx];

        if (item.readOnly) {
          this.goNextElement(currentPage, idx, onDone);
          return;
        }
        onDone(this.prepareFormItem(item));
      } else {
        this.goNextPage(currentPage, true, newPage => {
          if (newPage !== false) {
            this.goNextElement(newPage, -1, onDone);
          } else {
            onDone(false);
          }
        });
      }
    };

    goPrevElement = (currentPage, activeInputIdx, onDone) => {
      const pageElements = this.formStructure[currentPage];
      const idx = activeInputIdx - 1;
      if (idx >= 0) {
        const item = pageElements[idx];

        if (item.readOnly) {
          this.goPrevElement(currentPage, idx, onDone);
          return;
        }
        onDone(this.prepareFormItem(item));
      } else {
        this.goPrevPage(currentPage, true, newPage => {
          if (newPage !== false) {
            this.goPrevElement(
              newPage,
              this.formStructure[newPage].length,
              onDone
            );
          } else {
            onDone(false);
          }
        });
      }
    };

    goPrevPage = (currentPage, skipEmptyPages, onDone) => {
      const newPage = skipEmptyPages
        ? this.formStructure.length -
          1 -
          this.formStructure
            .slice()
            .reverse()
            .findIndex(
              (page, index) =>
                index >= this.formStructure.length - currentPage && page.length
            )
        : currentPage - 1;

      onDone(
        newPage === this.formStructure.length || newPage === -1
          ? false
          : newPage
      );
    };

    goNextPage = (currentPage, skipEmptyPages, onDone) => {
      const newPage = skipEmptyPages
        ? this.formStructure.findIndex(
            (page, index) => index > currentPage && page.length
          )
        : currentPage + 1;

      onDone(
        newPage === this.formStructure.length || newPage === -1
          ? false
          : newPage
      );
    };

    canAutoDoneOnBecomeValid = () => {
      return (
        this.props.document.isOnlyAcceptButton &&
        !findElementsWithoutValue(this.formStructure, this.formResult)
      );
    };

    onFormItemClicked = item => {
      this.setState({
        lastSelectedItem: item
      });
      if (item?.element === FORMELEMENTS_SIGNATURE) {
        this.onStartSignatureProcess(item);
      }
    };

    setLastSelectedItem = item => {
      this.setState({
        lastSelectedItem: item
      });
    };

    onFormItemFocused = item => {
      this.setLastSelectedItem(item);
    };

    onStartSignatureProcess = item => {
      this.setState({
        signatureProcessItem: item
      });
    };

    externalApiValidate = forceSkipSendConfirmation => {
      const validation = getExternalApiValidationConfig(this.props.document);

      if (!validation) {
        this.continueOnFormDone(forceSkipSendConfirmation);
        return;
      }

      gae(
        'FORMS',
        'START_EXT_VALIDATION',
        `${Object.keys(validation).length} endpoints`
      );
      this.setState({ externalApiFormErrors: new Map() }, () => {
        f7.dialog.preloader(II18.get('forms_external_validation_wait'));
        runExternalApiValidation({
          document: this.props.document,
          formStructure: this.formStructure,
          formData: this.formResult,
          recipientRequestId: this.props.recipientRequestId,
          templateMode: this.props.templateMode,
          onEndpointStart: (externalApiId, customIds) => {
            gae(
              'FORMS',
              'VALIDATION_API',
              externalApiId,
              `fieldsCount: ${customIds?.length}`
            );
          }
        }).then(result => {
          this.closeExternalApiValidationDialog();

          if (result.apiFailure) {
            gae('FORMS', 'EXT_VALIDATION_API_ERROR');
            alertDialog(II18.get('forms_external_validation_error'));
            return;
          }

          const externalApiFormErrors = result.fieldErrors || new Map();
          gae('FORMS', 'EXT_VALIDATION_RESULT', externalApiFormErrors.size);
          if (externalApiFormErrors.size) {
            // Merge before setState so the upcoming render already has
            // formErrors (instance field) for FormInput / getFirstErrorElement.
            // prepareLocalData only runs in didUpdate — too late for that paint.
            externalApiFormErrors.forEach((error, itemId) =>
              this.formErrors.set(itemId, { ...error, apiError: true })
            );
            this.setState({
              externalApiFormErrors,
              showRequiredErrors: true,
              moveToFirstError: true
            });
            return;
          }

          this.props.setDocumentForm(
            this.props.document.id,
            withExternalApiValidationStamp(this.formResult, result.stamp)
          );
          this.continueOnFormDone(forceSkipSendConfirmation, {
            stampCommitted: true
          });
        });
      });
    };

    /**
     * Soft-block when Done would advance before the form is actually ready.
     * Returns true if caller should stop (do not call onDocumentDone).
     * stampCommitted: stamp was just written via setDocumentForm; skip stamp
     * check so Redux prop lag does not false-trigger NEEDS_EXT_VALIDATION.
     */
    shouldSoftBlockFormAdvance = (stampCommitted = false) => {
      const document = this.props.document;
      if (document?.optional || document?.ready || document?.rejected) {
        return false;
      }

      const localValid =
        this.formStructure &&
        isDocumentFormValid(
          this.formStructure,
          this.formResult,
          this.props.requestedDocuments,
          document.id
        );

      if (!localValid) {
        gaeApp('ON_DOCUMENT_DONE_ERROR.DOCUMENT_IS_NOT_READY.LOCAL');
        return true;
      }

      if (
        !stampCommitted &&
        getExternalApiValidationConfig(document) &&
        !isExternalApiValidationStampValid(
          document,
          this.formStructure,
          this.formResult
        )
      ) {
        gaeApp(
          'ON_DOCUMENT_DONE_ERROR.DOCUMENT_IS_NOT_READY.NEEDS_EXT_VALIDATION'
        );
        return true;
      }

      return false;
    };

    continueOnFormDone = (forceSkipSendConfirmation, options) => {
      const stampCommitted = options?.stampCommitted === true;
      const notReadySignaturesOrFormReadyToSend = checkIfFormDoesNotNeedConfirm(
        this.formStructure,
        this.formResult
      );

      const skippingConfirm =
        forceSkipSendConfirmation === true ||
        notReadySignaturesOrFormReadyToSend === true;

      setTimeout(() => {
        if (this.shouldSoftBlockFormAdvance(stampCommitted)) {
          return;
        }

        const document = this.props.document;
        const needsLagBypass =
          !document?.optional && !document?.ready && !document?.rejected;

        this.props.onDocumentDone(
          skippingConfirm,
          Array.isArray(notReadySignaturesOrFormReadyToSend) &&
            getConfirmRestSignaturesMessage(
              notReadySignaturesOrFormReadyToSend
            ),
          needsLagBypass ? { allowAdvanceDespiteNotReady: true } : undefined
        );
      }, 300);
    };

    onFormDone = forceSkipSendConfirmation => {
      this.externalApiValidate(forceSkipSendConfirmation);
    };
    //prepareConditions =
    render() {
      const signatureItem = isFormReadyToSignature(this.getFormErrors());
      // ... and renders the wrapped component with the fresh data!
      // Notice that we pass through any additional props
      return (
        <>
          <WrappedComponent
            key={this.state.formRenderKey}
            onFormDone={this.onFormDone}
            onStartSignatureProcess={this.onStartSignatureProcess}
            onFormItemClicked={this.onFormItemClicked}
            onFormItemFocused={this.onFormItemFocused}
            lastSelectedItem={this.state.lastSelectedItem}
            setLastSelectedItem={this.setLastSelectedItem}
            signatureProcessItem={this.state.signatureProcessItem}
            onReceiveSignature={this.onReceiveSignature}
            onStopSignatureProcess={this.onStopSignatureProcess}
            canAutoDoneOnBecomeValid={this.canAutoDoneOnBecomeValid}
            formWasChanged={this.state.formWasChanged}
            formResult={this.formResult}
            formErrors={this.getFormErrors()}
            formStructure={this.formStructure}
            updateFormResult={this.updateFormResult}
            showStartButton={this.state.showStartButton}
            showActiveAnimation={this.state.showActiveAnimation}
            showRequiredErrors={this.state.showRequiredErrors}
            cancelMoveToFirstError={this.cancelMoveToFirstError}
            moveToFirstError={this.state.moveToFirstError}
            hideStartButton={this.hideStartButton}
            isFormValid={this.isFormValid}
            getFirstErrorElement={this.getFirstErrorElement}
            readyToSignatureItem={this.prepareFormItem(signatureItem)}
            readyToAcceptItem={this.prepareFormItem(
              isFormReadyToAccept(this.getFormErrors())
            )}
            pagesValid={this.arePagesIsValid()}
            formValid={this.isFormValid()}
            formActionTitleKey={getExternalApiFormActionTitleKey(
              this.props.document,
              this.formStructure,
              this.formResult,
              'title_done'
            )}
            goNextPage={this.goNextPage}
            goPrevPage={this.goPrevPage}
            goNextElement={this.goNextElement}
            goPrevElement={this.goPrevElement}
            pages={this.props.document.attachment}
            pdfMetaData={this.props.document.attributes.pdfMetaData}
            checkDataAndTryBack={this.checkDataAndTryBack}
            tryBack={this.tryBack}
            resetFormNow={this.resetFormNow}
            {...this.props}
            requestedDocuments={this.getRequestedDocumentsWithLiveFormData()}
            collectBioDataConfirmed={this.props.collectBioDataConfirmed.has(
              this.props.document?.id
            )}
            onCollectBioDataConfirmed={() =>
              this.props.setDocumentCollectBioDataConfirmed(
                this.props.document?.id
              )
            }
          />
        </>
      );
    }
  }

  WithFormsWrapper.propTypes = {
    collectBioDataConfirmed: PropTypes.object,
    document: PropTypes.object,
    formData: PropTypes.object,
    preparedFormsStructures: PropTypes.object,
    preparedFormsData: PropTypes.object,
    getESignatureType: PropTypes.func,
    startSignDocument: PropTypes.func,
    initWebsocket: PropTypes.func,
    setDocumentForm: PropTypes.func,
    smartFillFormMode: PropTypes.bool,
    recipientRequestId: PropTypes.string,
    templateMode: PropTypes.bool,
    setSignatureCache: PropTypes.func,
    setSignatureTouchData: PropTypes.func,
    setDocumentCollectBioDataConfirmed: PropTypes.func,
    cachedSignature: PropTypes.any,
    submitted: PropTypes.bool,
    documentIsQualifiedSigned: PropTypes.bool,
    navigateBack: PropTypes.func,
    onDocumentDone: PropTypes.func,
    documentWasVisited: PropTypes.bool,
    requestedDocuments: PropTypes.array,
    requestIsRestored: PropTypes.bool
  };

  const mapStateToProps = state => {
    return {
      collectBioDataConfirmed: state.collectBioDataConfirmed,
      cachedSignature:
        state.pwaCanApplySignature !== false && state.cachedSignature,
      requestIsRestored: state.requestIsRestored
    };
  };

  const mapDispatchToProps = dispatch => ({
    setSignatureCache: data => dispatch(setSignatureCache(data)),
    setSignatureTouchData: (formId, signatureId, data) =>
      dispatch(setSignatureTouchData(formId, signatureId, data)),
    setDocumentCollectBioDataConfirmed: formId =>
      dispatch(setDocumentCollectBioDataConfirmed(formId))
  });

  return xconnect(mapStateToProps, mapDispatchToProps)(WithFormsWrapper);
}
